import { Provider, SearchApi, SearchTablesApi } from '@repo/api-gw-sdk';
import type {
  EmailSearchFilter,
  FileSearchRecord,
  SearchFilesResponse,
  Configuration,
  Condition,
} from '@repo/api-gw-sdk';

import { toJsonText } from '@/components/queryBuilder/QueryLanguageConverter';

import type { HttpClient } from './httpClient';

const baseUrl = '/search';

// The index rows have no id of their own; the console keys them by resource + index path.
const withRowIds = (response: SearchFilesResponse) => ({
  ...response,
  data: response.data.map((file: FileSearchRecord) => ({
    ...file,
    id: file.resourceId + file.path,
  })),
});

const emptyOnNoResults = (error: unknown) => {
  if (error instanceof Object && 'code' in error && error.code === 404) {
    // No results found, not an error.
    return {
      data: [],
      pagination: {
        recordsCount: 0,
        nextToken: undefined,
        queryExecutionId: undefined,
      },
    } satisfies SearchFilesResponse;
  }
  throw error;
};

// A file browser rooted at a shared drive or SharePoint site reads shared-drive
// data, which is a different scope than a user's own drive.
export const addressesSharedDriveData = (pathPrefix: string) =>
  /^(shared_drives|sharepoint)\//.test(pathPrefix);

export const addressesTeamsData = (pathPrefix: string) =>
  pathPrefix.startsWith('teams/');

export const search = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const searchApi = new SearchApi(config);
  const queryDbApi = new SearchTablesApi(config);

  const workspaceFileSearch = (pathPrefix: string) => {
    if (addressesTeamsData(pathPrefix)) {
      return searchApi.searchWorkspaceTeamsFiles.bind(searchApi);
    }
    return addressesSharedDriveData(pathPrefix)
      ? searchApi.searchWorkspaceSharedDriveFiles.bind(searchApi)
      : searchApi.searchWorkspaceUserDriveFiles.bind(searchApi);
  };

  return {
    // Microsoft 365 file item search over the backup index (ADX), scoped by the
    // index path prefix of the browsed OneDrive, SharePoint or Teams namespace.
    m365Items: {
      useSearch: (
        term: string,
        pathPrefix: string,
        // Pins hits to one snapshot's indexed rows, so results match the
        // snapshot the explorer is browsing (and restore targets).
        snapshotId: string | undefined,
        limit: number,
        // Skip the request entirely (null SWR key) until a term is submitted.
        enabled = true,
        pageToken?: string,
        queryExecutionId?: string
      ) =>
        httpClient.execute(
          (
            ...args: Parameters<typeof searchApi.searchWorkspaceUserDriveFiles>
          ) =>
            workspaceFileSearch(pathPrefix)(...args).then(
              withRowIds,
              emptyOnNoResults
            ),
          enabled ? baseUrl : null,
          'm365Items',
          currentProjectId,
          {
            cloudProvider: Provider.Microsoft365,
            query: term,
            pathPrefix,
            snapshotId,
            limit,
            pageToken,
            // Part of the SWR key: a continuation page must not be served from another execution's result.
            queryExecutionId,
          }
        ),
    },
    // Microsoft 365 Exchange search over the backup index (ADX): the term is
    // matched against from/to/subject header attributes, since Exchange message
    // paths are opaque ids that a path search never matches.
    m365Mail: {
      useSearch: (
        term: string,
        pathPrefix: string,
        // Pins hits to one snapshot's indexed rows, so results match the
        // snapshot the explorer is browsing (and restore targets).
        snapshotId: string | undefined,
        limit: number,
        // Skip the request entirely (null SWR key) until a term is submitted.
        enabled = true,
        pageToken?: string,
        queryExecutionId?: string
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof searchApi.searchWorkspaceMail>) =>
            searchApi
              .searchWorkspaceMail(args[0], {
                ...args[1],
                queryExecutionId,
              })
              .then(withRowIds, emptyOnNoResults),
          enabled ? baseUrl : null,
          'm365Mail',
          currentProjectId,
          {
            cloudProvider: Provider.Microsoft365,
            emailSearchFilter: { text: [term] },
            pathPrefix,
            snapshotId,
            limit,
            pageToken,
          }
        ),
    },
    // Google Workspace Gmail search over the backup index (BigQuery), scoped to
    // one mailbox path prefix. A message path is an opaque id, so the term is
    // matched against the indexed subject attribute rather than the path.
    gwsMails: {
      useSearch: (
        term: string,
        pathPrefix: string,
        // Pins hits to one snapshot's indexed rows, so results match the
        // snapshot the explorer is browsing (and restore targets).
        snapshotId: string | undefined,
        limit: number,
        // Skip the request entirely (null SWR key) until a term is submitted.
        enabled = true,
        pageToken?: string,
        queryExecutionId?: string
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof searchApi.searchWorkspaceMail>) =>
            searchApi
              .searchWorkspaceMail(args[0], {
                ...args[1],
                queryExecutionId,
              })
              .then(withRowIds, emptyOnNoResults),
          enabled ? baseUrl : null,
          'gwsMails',
          currentProjectId,
          {
            cloudProvider: Provider.GoogleWorkspace,
            emailSearchFilter: { subject: [term] },
            pathPrefix,
            snapshotId,
            limit,
            pageToken,
          }
        ),
    },
    // Cross-entity search: no entity filter, so it spans every entity type
    // indexed for the provider. Entity-specific console modes use the typed
    // operations above instead.
    files: {
      useExecute: (
        term: string,
        limit: number,
        cloudProvider: Provider,
        queryExecutionId?: string,
        pageToken?: string,
        filters?: Condition,
        startDate?: Date,
        endDate?: Date
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof searchApi.searchFiles>) =>
            searchApi
              .searchFiles(args[0], {
                ...args[1],
                queryExecutionId,
              })
              .then(withRowIds, emptyOnNoResults),
          baseUrl,
          'files',
          currentProjectId,
          {
            cloudProvider,
            query: term,
            limit,
            pageToken,
            filters: filters ? toJsonText(filters) : undefined,
            startDate,
            endDate,
          }
        ),
    },
    // Mail search on the search page: the query is parsed into mail header
    // attributes, and matches any mailbox the caller may read.
    mails: {
      useExecute: (
        term: string,
        limit: number,
        cloudProvider: Provider,
        queryExecutionId?: string,
        pageToken?: string,
        startDate?: Date,
        endDate?: Date,
        emailSearchFilter?: EmailSearchFilter
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof searchApi.searchWorkspaceMail>) =>
            searchApi
              .searchWorkspaceMail(args[0], {
                ...args[1],
                queryExecutionId,
              })
              .then(withRowIds, emptyOnNoResults),
          baseUrl,
          'mails',
          currentProjectId,
          {
            cloudProvider,
            query: term,
            limit,
            pageToken,
            startDate,
            endDate,
            emailSearchFilter,
          }
        ),
    },
    users: {
      useExecute: (
        term: string,
        limit: number,
        cloudProvider: Provider,
        queryExecutionId?: string,
        pageToken?: string,
        snapshotId?: string,
        // Skip the request entirely (null SWR key) when disabled, so callers can
        // mount the hook unconditionally and only fetch once a term is submitted.
        enabled = true,
        startDate?: Date,
        endDate?: Date
      ) =>
        httpClient.execute(
          (
            ...args: Parameters<typeof searchApi.searchWorkspaceDirectoryUsers>
          ) =>
            searchApi
              .searchWorkspaceDirectoryUsers(args[0], {
                ...args[1],
                queryExecutionId,
              })
              .then(withRowIds, emptyOnNoResults),
          enabled ? baseUrl : null,
          'users',
          currentProjectId,
          {
            cloudProvider,
            query: term,
            limit,
            pageToken,
            startDate,
            endDate,
            snapshotId,
          }
        ),
    },
    sharedDrives: {
      useExecute: (
        term: string,
        limit: number,
        cloudProvider: Provider,
        queryExecutionId?: string,
        pageToken?: string,
        snapshotId?: string,
        // Skip the request entirely (null SWR key) when disabled, so callers can
        // mount the hook unconditionally and only fetch once a term is submitted.
        enabled = true,
        startDate?: Date,
        endDate?: Date
      ) =>
        httpClient.execute(
          (
            ...args: Parameters<
              typeof searchApi.searchWorkspaceDirectorySharedDrives
            >
          ) =>
            searchApi
              .searchWorkspaceDirectorySharedDrives(args[0], {
                ...args[1],
                queryExecutionId,
              })
              .then(withRowIds, emptyOnNoResults),
          enabled ? baseUrl : null,
          'sharedDrives',
          currentProjectId,
          {
            cloudProvider,
            query: term,
            limit,
            pageToken,
            startDate,
            endDate,
            snapshotId,
          }
        ),
    },
    groups: {
      useExecute: (
        term: string,
        limit: number,
        cloudProvider: Provider,
        queryExecutionId?: string,
        pageToken?: string,
        startDate?: Date,
        endDate?: Date
      ) =>
        httpClient.execute(
          (
            ...args: Parameters<typeof searchApi.searchWorkspaceDirectoryGroups>
          ) =>
            searchApi
              .searchWorkspaceDirectoryGroups(args[0], {
                ...args[1],
                queryExecutionId,
              })
              .then(withRowIds, emptyOnNoResults),
          baseUrl,
          'groups',
          currentProjectId,
          {
            cloudProvider,
            query: term,
            limit,
            pageToken,
            startDate,
            endDate,
          }
        ),
    },
    dbs: {
      useExecute: (
        term: string,
        cloudProvider: Provider,
        queryExecutionId?: string,
        pageToken?: string,
        pageIndex?: number,
        pageSize?: number
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof queryDbApi.searchTables>) =>
            queryDbApi.searchTables(
              args[0],
              {
                ...args[1],
                queryExecutionId,
              },
              args[2],
              args[3]
            ),
          baseUrl,
          'db',
          currentProjectId,
          {
            cloudProvider,
            query: term,
            pageToken,
          },
          pageIndex,
          pageSize
        ),
    },
  };
};
